import { useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { uniqBy } from 'lodash';

import {
  CreateEntertainerQuotePayload,
  EntertainerQuoteReview,
  EntertainerQuotes,
  ScheduleEntertainerQuotePayload,
  UpdateEntertainerQuotePayload,
} from '@admin/queries/AdminEntertainerQuotesQueries';

import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { Input } from '@components/form/Input';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { ImageGrid } from '@components/layout/ImageGrid/ImageGrid';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import {
  EntertainerQuotePopup,
  QuotePhotoOption,
  transformEntertainerToQuotePhotoOptions,
} from '../EntertainerQuotePopup';
import { EntertainerQuotePreviewPopup } from '../EntertainerQuotePreviewPopup';

const QuoteCard = <QuoteSource extends unknown>({
  review,
  onEditClick,
  onPreviewClick,
}: {
  review: EntertainerQuoteReview<QuoteSource>;
  onEditClick: () => void;
  onPreviewClick: () => void;
}) => {
  if (!review) {
    return <></>;
  }

  const hasQuotePost = Boolean(review?.quoteId);
  const isQuotePostPublished = review?.curatedQuote?.status === 'posted';

  const quoteButtonText = useMemo(() => {
    if (review?.curatedQuote?.status === 'failed') {
      return 'Failed';
    }
    if (review?.curatedQuote?.status === 'posted') {
      return 'Published';
    }

    if (review?.curatedQuote?.status === 'scheduled') {
      return 'Preview Scheduled Post';
    }

    return 'Preview & Publish';
  }, [review?.curatedQuote?.status]);

  const quoteByEntertainerTitle = review?.quoteByEntertainer?.title ?? 'Unknown';
  const quoteByEntertainerCities = review?.quoteByEntertainer?.cities?.length
    ? `${review.quoteByEntertainer.cities.map((city) => city.name).join(' • ')}`
    : '⚠️ No cities';

  return (
    <Card>
      <Card.Header
        title={quoteByEntertainerTitle}
        subtitle={`${dayjs(review.createdAt).fromNow()} — ${quoteByEntertainerCities}`}
        action={
          hasQuotePost && (
            <Button
              disabled={isQuotePostPublished}
              onClick={onEditClick}
              size="sm"
              className="py-2 px-3"
              variant="smooth"
              color="neutral"
            >
              Edit
            </Button>
          )
        }
      />
      <Card.Body>
        {review.question && (
          <div className="mb-2" style={{ fontStyle: 'italic', fontWeight: '500', opacity: 0.8 }}>
            {review.question}
          </div>
        )}
        <div>{review.text}</div>
      </Card.Body>
      <Card.Footer>
        {hasQuotePost ? (
          <Button
            disabled={isQuotePostPublished}
            startIcon={
              review?.curatedQuote?.status === 'failed' ? (
                <FontAwesomeIcon name="exclamation-triangle" />
              ) : undefined
            }
            color={review?.curatedQuote?.status === 'failed' ? 'danger' : undefined}
            onClick={onPreviewClick}
          >
            {quoteButtonText}
          </Button>
        ) : (
          <Button onClick={onEditClick}>Create Quote</Button>
        )}
      </Card.Footer>
    </Card>
  );
};

export const EntertainerQuotesSection = <QuoteSource extends unknown>({
  entertainerQuotes,
  previewUrl,
  onCreate,
  onUpdate,
  onSchedule,
  onAddEntertainerInstagramHandle,
  isAddingEntertainerInstagramHandle,
}: {
  entertainerQuotes: EntertainerQuotes<QuoteSource>;
  previewUrl: ({ quoteId, quoteFontSize }: { quoteId: string; quoteFontSize: number }) => string;
  onCreate: (
    payload: CreateEntertainerQuotePayload<QuoteSource>,
  ) => Promise<{ quote?: { _id: string } } | undefined>;
  onUpdate: (payload: UpdateEntertainerQuotePayload<QuoteSource>) => Promise<void>;
  onSchedule: (payload: ScheduleEntertainerQuotePayload) => Promise<void>;
  onAddEntertainerInstagramHandle?: (payload: {
    documentIds: string[];
    instagramHandle: string;
  }) => Promise<void>;
  isAddingEntertainerInstagramHandle?: boolean;
}) => {
  const { entertainer, entertainerFollowerCount, quotesAboutEntertainer } = entertainerQuotes;
  const [editingReview, setEditingReview] = useState<EntertainerQuoteReview<QuoteSource>>();
  const [previewingReview, setPreviewingReview] = useState<EntertainerQuoteReview<QuoteSource>>();
  const [instagramHandle, setInstagramHandle] = useState<string>('');
  const [instagramHandleError, setInstagramHandleError] = useState<string>();

  const handleUpdateInstagramHandle = async () => {
    setInstagramHandleError(undefined);
    try {
      await onAddEntertainerInstagramHandle?.({
        documentIds: quotesAboutEntertainer.map((review) => (review.source as any).documentId),
        instagramHandle,
      });
      setInstagramHandle('');
    } catch (e: any) {
      setInstagramHandleError(e.message);
    }
  };

  const images: QuotePhotoOption[] = transformEntertainerToQuotePhotoOptions(entertainer);

  return (
    <div key={entertainer._id || entertainer.title} style={{ marginBottom: 24 }}>
      <TitleToolbar
        className="mb-0"
        text={entertainer.title + (entertainer._id ? '' : ' ⚠️')}
        size="md"
        action={
          <IconButton
            variant="smooth"
            color="neutral"
            size="sm"
            style={{ margin: '-8px 0' }}
            onClick={() => {
              openExternalUrl(
                entertainer._id
                  ? `https://www.instagram.com/${entertainer.contact?.instagram}`
                  : `https://www.instagram.com/${quotesAboutEntertainer?.[0]?.quoteByEntertainer?.contact?.instagram}`,
              );
            }}
          >
            <FontAwesomeIcon name="instagram" faStyle="fab" />
          </IconButton>
        }
      />
      <div className="mb-4">{`${quotesAboutEntertainer.length} quote${quotesAboutEntertainer.length === 1 ? '' : 's'} • ${entertainerFollowerCount} followers`}</div>

      {!entertainer._id && (
        <div className="mb-2">
          <div className="d-flex align-items-center gap-2">
            <Input
              wrapperClassName="w-100"
              placeholder="@their.instagram.handle"
              value={instagramHandle}
              onChange={(e) => setInstagramHandle(e.target.value)}
            />
            <div>
              <Button
                roundness="rounded"
                style={{ height: 44 }}
                disabled={!instagramHandle.trim() || isAddingEntertainerInstagramHandle}
                onClick={handleUpdateInstagramHandle}
              >
                Update
              </Button>
            </div>
          </div>
          {instagramHandleError && <p className="text-danger mt-1 mb-0">{instagramHandleError}</p>}
        </div>
      )}

      {images.length > 0 && (
        <div className="mb-4">
          <ImageGrid images={images} />
        </div>
      )}

      {quotesAboutEntertainer.length > 1 ? (
        <HorizontalScroll
          items={quotesAboutEntertainer.map((review, i) => (
            <QuoteCard
              key={i}
              review={review}
              onEditClick={() => setEditingReview(review)}
              onPreviewClick={() => setPreviewingReview(review)}
            />
          ))}
        />
      ) : (
        <QuoteCard
          review={quotesAboutEntertainer?.[0]}
          onEditClick={() => setEditingReview(quotesAboutEntertainer?.[0])}
          onPreviewClick={() => setPreviewingReview(quotesAboutEntertainer?.[0])}
        />
      )}

      <EntertainerQuotePopup
        isVisible={!!editingReview}
        entertainerId={entertainer._id}
        review={editingReview!}
        photoOptions={images}
        onCreate={onCreate}
        onUpdate={onUpdate}
        onClose={() => setEditingReview(undefined)}
      />

      <EntertainerQuotePreviewPopup
        isVisible={!!previewingReview}
        review={previewingReview!}
        previewUrl={previewUrl}
        onSchedule={onSchedule}
        onClose={() => setPreviewingReview(undefined)}
      />
    </div>
  );
};
